import React from 'react';
import { FileText, AlertCircle, Users, Ban, Scale, Globe, Mail, ArrowLeft } from 'lucide-react';

interface TermsOfServiceProps {
  onBack?: () => void;
}

const TermsOfService: React.FC<TermsOfServiceProps> = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Button */}
        {onBack && (
          <button
            onClick={onBack}
            className="mb-6 flex items-center gap-2 text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to App
          </button>
        )}

        {/* Header */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-cyan-500 to-sky-500 rounded-xl shadow-lg mb-6">
            <FileText className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Terms of Service</h1>
          <p className="text-gray-600 dark:text-gray-400">Effective Date: January 9, 2025</p>
        </div>

        {/* Content */}
        <div className="prose prose-lg dark:prose-invert max-w-none">
          <section className="mb-10">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg p-6 border border-amber-200 dark:border-amber-800 mb-8">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Agreement to Terms</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">
                    By accessing or using FXPrompt, you agree to be bound by these Terms of Service. 
                    If you disagree with any part of these terms, you may not access the service.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-3">
              <Users className="w-6 h-6 text-cyan-500" />
              1. Account Terms
            </h2>
            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 space-y-4">
              <ul className="space-y-3 text-gray-600 dark:text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>You must be 13 years or older to use this service</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>You must provide accurate and complete registration information</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>You are responsible for maintaining the security of your account and password</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>You are responsible for all activities that occur under your account</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>One person or legal entity may not maintain more than one free account</span>
                </li>
              </ul>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">2. Acceptable Use</h2>
            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 space-y-4">
              <p className="text-gray-600 dark:text-gray-400 mb-4">You agree not to use FXPrompt to:</p>
              <ul className="space-y-3 text-gray-600 dark:text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>Generate content that violates any laws or regulations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>Create harmful, offensive, or inappropriate content</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>Infringe on intellectual property rights of others</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>Attempt to gain unauthorized access to the service</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>Interfere with or disrupt the service or servers</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>Use the service for any illegal or unauthorized purpose</span>
                </li>
              </ul>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">3. API Usage</h2>
            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 space-y-4">
              <ul className="space-y-3 text-gray-600 dark:text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>You are responsible for securing and managing your own API keys</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>FXPrompt is not responsible for charges incurred from third-party API usage</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>You must comply with the terms of service of all integrated API providers</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-cyan-500 mt-1">•</span>
                  <span>We do not store or have access to your API keys on our servers</span>
                </li>
              </ul>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">4. Content Rights</h2>
            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 space-y-4">
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Your Content</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    You retain all rights to the prompts and content you create using FXPrompt. 
                    We do not claim ownership of your generated content.
                  </p>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Service Content</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    The FXPrompt service, including its original content, features, and functionality, 
                    is owned by Flickmax.com and is protected by international copyright, trademark, 
                    and other intellectual property laws.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-3">
              <Ban className="w-6 h-6 text-cyan-500" />
              5. Termination
            </h2>
            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 space-y-4">
              <p className="text-gray-600 dark:text-gray-400">
                We may terminate or suspend your account immediately, without prior notice or liability, 
                for any reason, including without limitation if you breach the Terms.
              </p>
              <p className="text-gray-600 dark:text-gray-400">
                Upon termination, your right to use the service will cease immediately. 
                You may delete your account at any time from your profile settings.
              </p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-3">
              <Scale className="w-6 h-6 text-cyan-500" />
              6. Disclaimer & Limitation of Liability
            </h2>
            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 space-y-4">
              <p className="text-gray-600 dark:text-gray-400 uppercase font-semibold text-sm mb-3">
                The service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind.
              </p>
              <p className="text-gray-600 dark:text-gray-400">
                In no event shall Flickmax.com, FXPrompt, its directors, employees, partners, agents, 
                suppliers, or affiliates be liable for any indirect, incidental, special, consequential, 
                or punitive damages arising out of your use of the service.
              </p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">7. Changes to Terms</h2>
            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 space-y-4">
              <p className="text-gray-600 dark:text-gray-400">
                We reserve the right to modify or replace these Terms at any time. 
                If a revision is material, we will provide at least 30 days' notice prior to 
                any new terms taking effect.
              </p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-3">
              <Globe className="w-6 h-6 text-cyan-500" />
              8. Governing Law
            </h2>
            <div className="bg-gray-50 dark:bg-slate-800 rounded-lg p-6 space-y-4">
              <p className="text-gray-600 dark:text-gray-400">
                These Terms shall be governed and construed in accordance with the laws of the United States, 
                without regard to its conflict of law provisions. Our failure to enforce any right or 
                provision of these Terms will not be considered a waiver of those rights.
              </p>
            </div>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white flex items-center gap-3">
              <Mail className="w-6 h-6 text-cyan-500" />
              Contact Information
            </h2>
            <div className="bg-gradient-to-r from-cyan-50 to-sky-50 dark:from-cyan-900/20 dark:to-sky-900/20 rounded-lg p-6 border border-cyan-200 dark:border-cyan-800">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                If you have any questions about these Terms, please contact us:
              </p>
              <ul className="space-y-2 text-gray-600 dark:text-gray-400">
                <li>Email: <a href="mailto:legal@fxprompt.app" className="text-cyan-600 dark:text-cyan-400 hover:underline">legal@fxprompt.app</a></li>
                <li>Website: <a href="https://fxprompt.app" className="text-cyan-600 dark:text-cyan-400 hover:underline">fxprompt.app</a></li>
                <li>Company: Flickmax.com</li>
              </ul>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;